// Recipes list (left column of the Models panel). Pure presentation — the
// orchestrator owns loading, selection, filters, pagination, and actions.

import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { visibleRecipeTags } from "@/lib/manufacturing/variantConfig";
import { ghostBtnClass, adminDangerGhostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingModelsRecipeListPagination } from "./ManufacturingModelsRecipeListPagination";
import { ManufacturingModelsRecipeListToolbar } from "./ManufacturingModelsRecipeListToolbar";
import { isRecipeConfirmEligible } from "./useManufacturingModelsConfirmReview";
import type { ModelsActiveFilter } from "./useManufacturingModelsListView";
import type { ModelsListPageSize } from "./useManufacturingModelsListPagination";

export function ManufacturingModelsRecipeList({
  recipes,
  pageItems,
  selectedId,
  creating,
  loading,
  pendingCounts,
  search,
  onSearchChange,
  activeFilter,
  onActiveFilterChange,
  needsReviewOnly,
  onNeedsReviewOnlyChange,
  recipeGroupId,
  onRecipeGroupIdChange,
  recipeGroups,
  page,
  pageSize,
  pageCount,
  rangeStart,
  rangeEnd,
  filteredCount,
  onSetPage,
  onSetPageSize,
  onPick,
  showDuplicate,
  canPermanentlyDelete,
  onDuplicate,
  onRequestPermanentDelete,
  bulkEnabled = false,
  bulkSelectedIds,
  onToggleBulk,
  onSelectAllFiltered,
  allFilteredSelected,
  confirmBusy = false,
  onConfirmReview,
}: {
  recipes: MfgRecipeSummary[];
  pageItems: MfgRecipeSummary[];
  selectedId: string | null;
  creating: boolean;
  loading: boolean;
  pendingCounts: Record<string, number>;
  search: string;
  onSearchChange: (next: string) => void;
  activeFilter: ModelsActiveFilter;
  onActiveFilterChange: (next: ModelsActiveFilter) => void;
  needsReviewOnly?: boolean;
  onNeedsReviewOnlyChange?: (next: boolean) => void;
  recipeGroupId?: string;
  onRecipeGroupIdChange?: (next: string) => void;
  recipeGroups?: ReadonlyArray<{ id: string; code: string; name: string }>;
  page: number;
  pageSize: ModelsListPageSize;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  filteredCount: number;
  onSetPage: (next: number) => void;
  onSetPageSize: (next: ModelsListPageSize) => void;
  onPick: (id: string) => void;
  showDuplicate: boolean;
  canPermanentlyDelete: boolean;
  onDuplicate: (id: string) => void;
  onRequestPermanentDelete: (recipe: MfgRecipeSummary) => void;
  bulkEnabled?: boolean;
  bulkSelectedIds?: Set<string>;
  onToggleBulk?: (id: string) => void;
  onSelectAllFiltered?: () => void;
  allFilteredSelected?: boolean;
  confirmBusy?: boolean;
  onConfirmReview?: (recipeId: string) => void;
}) {
  const paginationProps = {
    page,
    pageCount,
    pageSize,
    rangeStart,
    rangeEnd,
    total: filteredCount,
    canPrev: page > 0,
    canNext: page + 1 < pageCount,
    onSetPage,
    onSetPageSize,
  };

  return (
    <div className="space-y-3">
      <ManufacturingModelsRecipeListToolbar
        search={search}
        onSearchChange={onSearchChange}
        activeFilter={activeFilter}
        onActiveFilterChange={onActiveFilterChange}
        needsReviewOnly={needsReviewOnly}
        onNeedsReviewOnlyChange={onNeedsReviewOnlyChange}
        recipeGroupId={recipeGroupId}
        onRecipeGroupIdChange={onRecipeGroupIdChange}
        recipeGroups={recipeGroups}
        bulkEnabled={bulkEnabled}
        filteredCount={filteredCount}
        allFilteredSelected={allFilteredSelected}
        onSelectAllFiltered={onSelectAllFiltered}
      />

      {loading ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading models…</div>
      ) : recipes.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No models yet. Use + New model.
        </div>
      ) : filteredCount === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No models match your search or filter.
        </div>
      ) : (
        <>
          <ManufacturingModelsRecipeListPagination {...paginationProps} variant="top" />
          <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
            {pageItems.map((r) => {
              const isActive = selectedId === r.id && !creating;
              const pending = pendingCounts[r.id] ?? 0;
              const bulkOn = Boolean(bulkSelectedIds?.has(r.id));
              const visibleTags = visibleRecipeTags(Array.isArray(r.tags) ? r.tags : []);
              return (
                <li key={r.id}>
                  <div
                    className={
                      "flex items-stretch gap-1 rounded-md transition-colors " +
                      (isActive
                        ? "bg-indigo-50 dark:bg-indigo-950/40"
                        : "hover:bg-gray-50 dark:hover:bg-zinc-800/60")
                    }
                  >
                    {bulkEnabled && onToggleBulk ? (
                      <label className="flex shrink-0 items-center pl-2">
                        <input
                          type="checkbox"
                          checked={bulkOn}
                          onChange={() => onToggleBulk(r.id)}
                          aria-label={`Select ${r.code}`}
                        />
                      </label>
                    ) : null}
                    <button
                      type="button"
                      className={
                        "min-w-0 flex-1 rounded-md px-3 py-2 text-left text-sm " +
                        (isActive
                          ? "text-indigo-900 dark:text-indigo-100"
                          : "text-gray-800 dark:text-zinc-200")
                      }
                      onClick={() => onPick(r.id)}
                    >
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-medium">
                          {r.code} · {r.name}
                        </span>
                        {r.needs_review ? (
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                            Needs review
                          </span>
                        ) : null}
                        {pending > 0 ? (
                          <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                            Pending {pending}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-zinc-400">
                        <span className="capitalize">{(r.kind as MfgRecipeKind) ?? "assembly"}</span>
                        {" · "}
                        {r.is_active ? "Active" : "Inactive"}
                        {visibleTags.length > 0 ? " · " + visibleTags.join(", ") : ""}
                      </div>
                    </button>
                    {showDuplicate || canPermanentlyDelete || onConfirmReview ? (
                      <div className="flex shrink-0 flex-col justify-center gap-1 pr-2">
                        {onConfirmReview && isRecipeConfirmEligible(r, pendingCounts) ? (
                          <button
                            type="button"
                            className={`${mfgPrimaryBtnClass} px-2 py-0.5 text-[11px]`}
                            disabled={confirmBusy}
                            onClick={() => onConfirmReview(r.id)}
                          >
                            Confirm
                          </button>
                        ) : null}
                        {showDuplicate ? (
                          <button
                            type="button"
                            className={`${ghostBtnClass} px-2 py-0.5 text-[11px]`}
                            onClick={() => onDuplicate(r.id)}
                          >
                            Duplicate
                          </button>
                        ) : null}
                        {canPermanentlyDelete && !r.is_active ? (
                          <button
                            type="button"
                            className={`${adminDangerGhostBtnClass} px-2 py-0.5 text-[11px]`}
                            onClick={() => onRequestPermanentDelete(r)}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
          <ManufacturingModelsRecipeListPagination {...paginationProps} variant="bottom" />
        </>
      )}
    </div>
  );
}
